/**
 * Phone taps often end a few dozen pixels outside the face. Phaser then
 * emits pointerout and a scene pointerup, and never pointerup on the button.
 * The lower results button ("Return to glen") is where a thumb lifts, so that miss
 * left the results screen stuck. A press stays armed until the pointer comes
 * up, and a release within this many pixels of the hit still counts.
 */
export const RELEASE_SLOP = 48;

export interface PressPointer {
  id: number;
  worldX: number;
  worldY: number;
}

export interface PressHit {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ButtonPressHost {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  displayOriginX: number;
  displayOriginY: number;
  input: { hitArea: PressHit | null } | null;
  /**
   * World position of a button that lives in a scrolled container.
   * `x` and `y` are local, so a lift beside the face would miss without this.
   */
  getWorldTransformMatrix?: () => { tx: number; ty: number; a?: number; b?: number; c?: number; d?: number };
  on(event: string, fn: (pointer: PressPointer) => void): void;
  once(event: string, fn: () => void): void;
}

export interface WorldRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ButtonPressInput {
  on(event: string, fn: (pointer: PressPointer) => void): void;
  off(event: string, fn: (pointer: PressPointer) => void): void;
}

export interface ButtonPressHandlers {
  onPress: () => void;
  /** `activate` is false when the finger came up too far from the face to count. */
  onRelease: (activate: boolean) => void;
}

function placedAt(host: ButtonPressHost): { x: number; y: number } {
  const matrix = host.getWorldTransformMatrix?.();
  if (matrix && Number.isFinite(matrix.tx) && Number.isFinite(matrix.ty)) return { x: matrix.tx, y: matrix.ty };
  return { x: host.x, y: host.y };
}

/** World bounds of the hit, so a press can finish after the face is destroyed. */
export function worldHitRect(host: ButtonPressHost): WorldRect | null {
  const area = host.input?.hitArea;
  if (!area) return null;
  const matrix = host.getWorldTransformMatrix?.();
  const useMatrix = Boolean(matrix && Number.isFinite(matrix.tx) && Number.isFinite(matrix.ty));
  const corners: Array<[number, number]> = [
    [area.x, area.y],
    [area.x + area.width, area.y],
    [area.x, area.y + area.height],
    [area.x + area.width, area.y + area.height],
  ];
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const [hx, hy] of corners) {
    const lx = hx - host.displayOriginX;
    const ly = hy - host.displayOriginY;
    const scaleX = host.scaleX || 1;
    const scaleY = host.scaleY || 1;
    const wx = useMatrix
      ? (matrix?.a ?? scaleX) * lx + (matrix?.b ?? 0) * ly + (matrix?.tx ?? host.x)
      : host.x + lx * scaleX;
    const wy = useMatrix
      ? (matrix?.c ?? 0) * lx + (matrix?.d ?? scaleY) * ly + (matrix?.ty ?? host.y)
      : host.y + ly * scaleY;
    left = Math.min(left, wx);
    right = Math.max(right, wx);
    top = Math.min(top, wy);
    bottom = Math.max(bottom, wy);
  }
  return { left, top, right, bottom };
}

function insideRect(rect: WorldRect | null, pointer: PressPointer, slop: number): boolean {
  if (!rect) return false;
  return (
    pointer.worldX >= rect.left - slop &&
    pointer.worldX <= rect.right + slop &&
    pointer.worldY >= rect.top - slop &&
    pointer.worldY <= rect.bottom + slop
  );
}

/** Same space Phaser uses: translate into the object, then add the display origin. */
export function releaseInside(host: ButtonPressHost, pointer: PressPointer, slop = RELEASE_SLOP): boolean {
  const area = host.input?.hitArea;
  if (!area) return false;
  const scaleX = host.scaleX || 1;
  const scaleY = host.scaleY || 1;
  const placed = placedAt(host);
  const localX = (pointer.worldX - placed.x) / scaleX + host.displayOriginX;
  const localY = (pointer.worldY - placed.y) / scaleY + host.displayOriginY;
  return (
    localX >= area.x - slop &&
    localX <= area.x + area.width + slop &&
    localY >= area.y - slop &&
    localY <= area.y + area.height + slop
  );
}

/**
 * Arm on pointerdown. Do not disarm on pointerout.
 * Activate on the object's pointerup, or on the scene pointerup / pointerupoutside
 * when the lift is still within {@link RELEASE_SLOP} of the hit.
 * The object event and the scene event both arrive for a clean tap; only the first runs.
 */
export function bindButtonPress(input: ButtonPressInput, host: ButtonPressHost, handlers: ButtonPressHandlers): void {
  let down = false;
  let destroyed = false;
  let pointerId = -1;
  let armed: WorldRect | null = null;

  const detach = () => {
    input.off('pointerup', onUp);
    input.off('pointerupoutside', onUp);
  };

  const finish = (pointer: PressPointer, forceInside: boolean) => {
    if (!down || pointer.id !== pointerId) return;
    // A repaint can destroy the face between the press and the lift. The finger
    // still came up on the spot that was pressed, so the tap counts.
    const activate = destroyed ? insideRect(armed, pointer, RELEASE_SLOP) : forceInside || releaseInside(host, pointer);
    down = false;
    pointerId = -1;
    armed = null;
    detach();
    handlers.onRelease(activate);
  };

  const onUp = (pointer: PressPointer) => finish(pointer, false);

  host.on('pointerdown', (pointer) => {
    down = true;
    pointerId = pointer.id;
    armed = worldHitRect(host);
    handlers.onPress();
  });
  host.on('pointerup', (pointer) => finish(pointer, true));
  input.on('pointerup', onUp);
  input.on('pointerupoutside', onUp);
  host.once('destroy', () => {
    destroyed = true;
    if (!down) detach();
  });
}
